// components/rjsf-customizations/CustomTemplates.tsx
import React, {ReactNode, useContext} from 'react';
import { cn } from "@/lib/utils";

// ייבוא טיפוסים רלוונטיים מ-RJSF
import {
    ArrayFieldTemplateItemType,
    ArrayFieldTemplateProps, ArrayFieldTitleProps,
    DescriptionFieldProps, FieldHelpProps,
    FieldTemplateProps, ObjectFieldTemplateProps, RJSFSchema,
    RJSFValidationError,
    StrictRJSFSchema,
    TemplatesType, TitleFieldProps,
    UiSchema
} from '@rjsf/utils';
import {FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form.tsx";
import {Checkbox} from "@/components/ui/checkbox.tsx";
import {DescriptionSetting} from "@/components/DescriptionSetting.tsx";
import {useFormContext} from "react-hook-form";
import {Button} from "@/components/ui/button.tsx";
import {PlusIcon, TrashIcon} from "@heroicons/react/24/outline";
import collect from "collect.js";
import {ScrollBar, ScrollArea} from "@/components/ui/scroll-area.tsx";
import {ChevronUpIcon, ChevronDownIcon} from "lucide-react";


/**
 * תבנית שדה מותאמת אישית. עוטפת את הווידג'ט בלייבל, תיאור, עזרה ושגיאות.
 */
export function CustomFieldTemplate<T = any, S extends StrictRJSFSchema = StrictRJSFSchema, U extends UiSchema = UiSchema>(props: FieldTemplateProps<T, S, U>) {
    const {
        id,
        label,
        children, // זהו הווידג'ט (הקלט עצמו)
        errors, // רכיב React שמכיל רשימת שגיאות
        help, // רכיב React שמכיל טקסט עזרה
        description, // רכיב React שמכיל תיאור השדה
        hidden,
        required,
        displayLabel,
        schema, // גישה לסכמה כדי לבדוק טיפוסים
    } = props;

    const parentSchema = useContext(ArrayFieldContext)?.parent;

    if (hidden) {
        return <div className="hidden">{children}</div>;
    }

    // Check if the schema type is boolean, which might be a single checkbox
    const isBooleanSchema = schema.type === 'boolean';

    const form = useFormContext();

    if(props.id === "root") {
        return (
            <div className="grid grid-cols-2 gap-x-4 gap-y-6">
                {children}
            </div>
        )
    }


    //check if id ends with underscore and number
    if (schema.type === 'object' && /_\d+$/.test(id)) {
        return children;
    }

    const isInnerRepeater = !!parentSchema;
    const isTableRepeater = isInnerRepeater ? parentSchema.format === 'table' : false

    const isDev = import.meta.env.DEV;


    function getName() {

        const idField = id.replace(/^root_/, "");

        if (isInnerRepeater) {
            return idField.replace(/_(\d+?)_/, "[$1].");
        }
        return idField;
    }

    return (
        <FormField
            control={form.control}
            name={'settings.'+getName()} // הסרת root_ מהשם
            render={() => (
                <FormItem tag={isTableRepeater ? 'td' : 'div'} className={cn(isTableRepeater ? "py-0.5 px-0.5 text-start text-xs tracking-wider" : "col-span-2 space-y-2")}>
                    <div className={cn(props.schema.type !== 'boolean' ? "space-y-2" : 'flex gap-2 items-center', {
                        'justify-center' : isTableRepeater && schema.type === 'boolean',
                        'min-w-[80px]': isTableRepeater && schema.type !== 'boolean',
                    })}>
                        {props.schema.type === 'boolean' && children}
                        {!isTableRepeater &&
                            <FormLabel className="flex gap-1 items-center text-xs">
                                <span>{props.label} {props.rawDescription && description }</span>
                                {isDev && <span className="text-xs text-gray-500 italic">({props.id})</span>}
                            </FormLabel>
                        }
                        {props.schema.type !== 'boolean' && children}
                        {props.schema.type !== 'boolean' && <FormMessage/>}
                    </div>
                    {props.schema.type === 'boolean' && <FormMessage/>}
                </FormItem>
            )}
        />

        // <div className={cn("space-y-2", (errors && (errors.props as any)?.errors && (errors.props as any)?.errors.length > 0) && "text-red-500")}>
        //     {/* הצגת הלייבל - לא עבור Checkbox שמרנדר את הלייבל שלו צמוד אליו */}
        //     {displayLabel && !isBooleanSchema && (
        //         <Label
        //             htmlFor={id}
        //             className={cn(required && "after:content-['*'] after:ml-0.5 after:text-red-500")}
        //         >
        //             {label}
        //         </Label>
        //     )}
        //     {/* הצגת תיאור (אופציונלי) */}
        //     {description}
        //     {/* הווידג'ט עצמו עטוף בדיב */}
        //     {/* Flexible layout for widgets, particularly for boolean (checkbox) */}
        //     <div className={cn("flex items-center", isBooleanSchema && "space-x-2")}>
        //         {children} {/* הווידג'ט מרונדר כאן */}
        //
        //         {/* אם זה Checkbox Schema, נציג את הלייבל כאן, צמוד ל Checkbox */}
        //         {isBooleanSchema && displayLabel && (
        //             <Label
        //                 htmlFor={id}
        //                 className={cn(required && "after:content-['*'] after:ml-0.5 after:text-red-500")}
        //             >
        //                 {label}
        //             </Label>
        //         )}
        //     </div>
        //
        //
        //     {/* הצגת טקסט עזרה (אופציונלי) */}
        //     {help}
        //
        //     {/* הצגת שגיאות ולידציה של RJSF */}
        //     {/* RJSF provides an <ErrorList> component by default via the `errors` prop */}
        //     {/* Check if there are actual errors to display */}
        //     {(errors && (errors.props as any)?.errors && (errors.props as any)?.errors.length > 0) && (
        //         // RJSF's default errors prop is a React element rendering a ul/li.
        //         // We can either render it directly or extract the errors and render them as <p> tags.
        //         // Rendering as <p> tags with Shadcn FormMessage style:
        //         <div className="text-red-500 text-sm mt-1 space-y-1">
        //             {(errors.props as any).errors.map((error: RJSFValidationError, i: number) => (
        //                 <p key={i}>{error.stack}</p> // Use error.stack or error.message
        //             ))}
        //         </div>
        //         // Or render the default error list component:
        //         // {errors}
        //     )}

        // </div>
    );
}

function CustomDescriptionFieldTemplate(props: DescriptionFieldProps) {
    return (
        <DescriptionSetting>
            {props.description}
        </DescriptionSetting>
    )
}


function CustomHelpFieldTemplate(props: FieldHelpProps) {
    return (
        <DescriptionSetting>
            {props.help}
        </DescriptionSetting>
    );
}

const ArrayFieldContext = React.createContext<null|{parent: RJSFSchema}>(null);

function CustomArrayFieldTemplate(props: ArrayFieldTemplateProps) {
    const {items, className, uiSchema, canAdd, schema, onAddClick, title } = props;

    const id = props.idSchema.$id.replace(/^root_/, "");
    const form = useFormContext();

    const fieldTitle = uiSchema?.["ui:title"] || title || schema.title;

    console.log(props)
    if(items.length === 0 && canAdd) return (
            <Button
                className="w-full"
                variant="outline"
                onClick={onAddClick}
            >
                {fieldTitle ? "הוסף "+fieldTitle : 'הוסף'}
            </Button>
    );

    if(schema.format === 'table'
        && schema.items
        && typeof schema.items === 'object'
        && "type" in schema.items
        && schema.items.type === 'object'
        && schema.items?.properties
    ) {
        return (
            <ArrayFieldContext value={{parent: schema}}>
                <ScrollArea dir="rtl" className="w-[420px] whitespace-nowrap">
                    <table className="max-w-min w-full table-auto">
                        <thead>
                            <tr className="bg-gray-100">
                                <th></th>
                                {collect(schema.items.properties).map((item, key) => {
                                    return (
                                        <th key={key} className="py-0.5 px-2 text-start text-xs font-medium uppercase tracking-wider">
                                            {item.title}
                                        </th>
                                    )
                                }).values<ReactNode>().all()}
                                <th></th>
                            </tr>
                        </thead>

                        <tbody>
                        {items.map((item: ArrayFieldTemplateItemType, i) => {
                            return <tr key={item.key}>
                                <td>
                                    <div className="flex flex-col">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            disabled={!item.hasMoveUp}
                                            size="icon-xs"
                                            className="opacity-60 hover:opacity-100 disabled:opacity-10 disabled:hover:opacity-10 rounded-b-none h-4"
                                            onClick={item.onReorderClick(item.index, item.index - 1)}
                                            aria-label={`Move ${fieldTitle || 'item'} ${item.index + 1} up`}
                                        >
                                            <ChevronUpIcon className="size-4" />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon-xs"
                                            className="opacity-60 hover:opacity-100  disabled:opacity-10 disabled:hover:opacity-10  rounded-t-none  h-4"
                                            disabled={!item.hasMoveDown}
                                            onClick={item.onReorderClick(item.index, item.index + 1)}
                                            aria-label={`Move ${fieldTitle || 'item'} ${item.index - 1} down`}
                                        >
                                            <ChevronDownIcon className="size-4" />
                                        </Button>
                                    </div>
                                </td>
                                {item.children}
                                <td>
                                    <div className="flex gap-1">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon-xs"
                                            className="text-red-500 hover:text-red-700" // Adjust margin as needed
                                            onClick={item.onDropIndexClick(item.index)}
                                            aria-label={`Remove ${fieldTitle || 'item'} ${item.index + 1}`}
                                        >
                                            <TrashIcon className="size-4" />
                                        </Button>

                                        {i === items.length - 1 && canAdd ? (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon-xs"
                                                onClick={onAddClick}
                                                aria-label={`Add ${fieldTitle || 'item'}`}
                                            >
                                                <PlusIcon/>
                                            </Button>
                                        ) : (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon-xs"
                                                className="opacity-0"
                                            >
                                                <PlusIcon/>
                                            </Button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        })}

                        </tbody>
                    </table>
                    <ScrollBar orientation="horizontal" className="!-bottom-2"/>
                </ScrollArea>
            </ArrayFieldContext>
        )
    }

    return (
        <ArrayFieldContext value={{parent: schema}}>
            <div>
                <div>
                    {items && items.map((item: ArrayFieldTemplateItemType) => {
                        const {children, key, onDropIndexClick, index} = item;
                        return (
                            <div key={key} className="flex gap-4">
                                <div className="flex-grow [&>fieldset]:flex [&>fieldset]:gap-4">
                                    {children}
                                </div>
                                <div className="flex gap-1">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon-xs"
                                        className="text-red-500 hover:text-red-700" // Adjust margin as needed
                                        onClick={onDropIndexClick(index)}
                                        aria-label={`Remove ${fieldTitle || 'item'} ${index + 1}`}
                                    >
                                        <TrashIcon className="size-4" />
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon-xs"
                                        className="text-gray-500 hover:text-gray-700" // Adjust margin as needed
                                        onClick={() => form.setValue(id, [...form.getValues(id).slice(0, index), ...form.getValues(id).slice(index + 1)])}
                                        aria-label={`Remove ${fieldTitle || 'item'} ${index + 1}`}
                                    >
                                        <TrashIcon className="size-4" />
                                    </Button>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </ArrayFieldContext>
    )
}


function CustomArrayFieldTitleTemplate(props: ArrayFieldTitleProps) {
    return null;
}


function CustomArrayFieldItemTemplate(props: ArrayFieldTemplateItemType) {
    return props.children;
}

function CustomTitleFieldTemplate(props: TitleFieldProps) {
    if(props.schema.type === 'object' && /_\d+__title$/.test(props.id)) {
        return null;
    }
    return <FormLabel className="text-xs">{props.title}</FormLabel>;
}

function CustomObjectFieldTemplate(props: ObjectFieldTemplateProps) {
    return props.properties.map((prop) => {
        return prop.content;
    })
}

// אובייקט הממפה את שמות התבניות הסטנדרטיות של RJSF לרכיבים המותאמים אישית שלנו
export const customTemplates = {
    FieldTemplate: CustomFieldTemplate,
    ObjectFieldTemplate: CustomObjectFieldTemplate, // אם תרצה להתאים
    ArrayFieldTemplate: CustomArrayFieldTemplate,   // אם תרצה להתאים
    TitleFieldTemplate: CustomTitleFieldTemplate,   // אם תרצה להתאים
    DescriptionFieldTemplate: CustomDescriptionFieldTemplate, // אם תרצה להתאים
    // ErrorListTemplate: CustomErrorListTemplate,     // אם תרצה להתאים
    HelpFieldTemplate: CustomHelpFieldTemplate, // אם תרצה להתאים
    // ArrayFieldTitleTemplate: CustomArrayFieldTitleTemplate, // אם תרצה להתאים
    ArrayFieldItemTemplate: CustomArrayFieldItemTemplate, // אם תרצה להתאים
};

// אופציונלי: התאמה אישית של רכיבי ה Help, Description, Title, ErrorList עצמם
// ייבוא טיפוסים עבור רכיבים בסיסיים אלה
// import { DescriptionFieldProps, TitleFieldProps, HelpFieldProps, ErrorListProps } from '@rjsf/utils';

// export function CustomDescriptionField(props: DescriptionFieldProps) { return <p className="text-sm text-muted-foreground mt-1">{props.description}</p>; }
// export function CustomTitleField(props: TitleFieldProps) { return <h4 className="text-base font-semibold mt-4 mb-2">{props.title}</h4>; }
// export function CustomHelpField(props: HelpFieldProps) { return <p className="text-xs text-gray-500 mt-1 italic">{props.help}</p>; }
// export function CustomErrorList(props: ErrorListProps) {
//   const { errors } = props;
//   return (
//     <div className="text-red-500 text-sm space-y-1">
//       <p>שגיאות:</p>
//       <ul>{errors.map((error, i) => <li key={i}>{error.stack}</li>)}</ul>
//     </div>
//   );
// }

// אם הגדרת רכיבים בסיסיים מותאמים אישית, הרשום אותם כאן:
// export const customBaseComponents = {
//   DescriptionField: CustomDescriptionField,
//   TitleField: CustomTitleField,
//   HelpField: CustomHelpField,
//   ErrorList: CustomErrorList,
// };